import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, LogOut, MoveRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { logoutUser } from '../../features/auth/authThunks';
import { selectLegacyProfile } from '../../features/auth/authSelectors';
import { goToLegacyFront, legacyFrontUrl } from '../../features/auth/legacyFront';

/** So the automatic jump happens once per tab and not on every way back in. */
const ATTEMPTED_KEY = 'legacyRedirectAttempted';

const COPY = {
  admin_checkout: {
    title: 'Tu panel está en la plataforma actual',
    body: 'Tu cuenta trabaja con retiros y conductores, y eso todavía se gestiona en la plataforma de siempre. Te llevamos para allá.',
  },
  driver: {
    title: 'Tus rutas están en la plataforma actual',
    body: 'Las entregas del día y la carga de evidencias siguen en la plataforma de siempre. Te llevamos para allá.',
  },
};

const attempted = () => {
  try {
    return sessionStorage.getItem(ATTEMPTED_KEY) === '1';
  } catch (error) {
    // Private windows and blocked storage: better to jump twice than never.
    return false;
  }
};

const markAttempted = () => {
  try {
    sessionStorage.setItem(ATTEMPTED_KEY, '1');
  } catch (error) {
    /* nothing to do: the jump matters more than remembering it */
  }
};

/**
 * The handover to the front this app is replacing.
 *
 * Shown to the profiles that have no workspace here — the checkout plan and the
 * driver. It sits outside `AppShell` on purpose: that is what mounts the
 * notifications socket and the sidebar, and opening a socket for an account we
 * are about to eject is work nobody asked for.
 *
 * Written with its own markup rather than `ErrorState`, which paints its icon
 * in `bg-red-50 / text-danger`. That is right for `UnsupportedProfile`, which
 * really is a dead end. Nothing failed here.
 *
 * It **names the host** it is sending people to. During a migration that is the
 * one thing that turns "se me quedó en blanco" into a report somebody can act
 * on.
 *
 * The session is left alone. Clearing it would race the jump — `logoutUser`
 * ends in a navigation of its own — and would turn this into a human loop: log
 * in, get sent away, come back, log in again, with nothing ever explaining why.
 * Kept, this screen is stable: reload it, reopen the tab, follow a bookmark,
 * same explanation. Which also makes "Cerrar sesión" the only door back to the
 * login, since `/login` with a session open bounces to `/dashboard` and lands
 * right back here.
 */
export const LegacyRedirect = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profile = useSelector(selectLegacyProfile);
  const [url] = useState(legacyFrontUrl);

  const copy = COPY[profile] || COPY.admin_checkout;
  // `null` means the only address we could resolve is our own, which happens
  // locally when both fronts fight over port 3000. Better said out loud than
  // reloaded forever.
  const canRedirect = Boolean(url);

  useEffect(() => {
    if (!canRedirect || attempted()) return undefined;

    const timer = setTimeout(() => {
      // Written here and not on mount: StrictMode mounts twice in development,
      // and a flag set on the first pass would cancel the jump on the second.
      markAttempted();
      goToLegacyFront();
    }, 2500);

    return () => clearTimeout(timer);
  }, [canRedirect]);

  const handleLogout = () => {
    try {
      sessionStorage.removeItem(ATTEMPTED_KEY);
    } catch (error) {
      /* the mark is a convenience, not a guarantee */
    }
    dispatch(logoutUser(navigate));
  };

  return (
    <div
      data-testid="legacy-redirect"
      className="min-h-screen flex items-center justify-center bg-ninja-gray dark:bg-[#0f0f0f] p-4"
    >
      <div className="w-full max-w-md flex flex-col items-center text-center">
        <div className="w-16 h-16 bg-ninja-greenLight rounded-2xl flex items-center justify-center mb-5 border border-ninja-green/20">
          <MoveRight size={28} className="text-ninja-green" />
        </div>

        <h1 className="text-xl font-bold text-ninja-dark dark:text-white mb-2">{copy.title}</h1>
        <p className="text-text-secondary mb-2">{copy.body}</p>

        {canRedirect ? (
          <p className="text-xs text-text-muted mb-6">
            Destino: <span data-testid="legacy-redirect-host">{url}</span>
          </p>
        ) : (
          <p className="text-xs text-text-muted mb-6">
            No pudimos resolver la dirección de la plataforma actual desde este equipo.
          </p>
        )}

        <div className="flex flex-wrap items-center justify-center gap-3">
          {canRedirect && (
            <a
              href={url}
              data-testid="legacy-redirect-link"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-ninja-green hover:bg-ninja-green/90 text-white shadow-sm transition-colors"
            >
              Ir ahora
              <ExternalLink size={16} />
            </a>
          )}

          <Button variant="ghost" icon={LogOut} onClick={handleLogout}>
            Cerrar sesión
          </Button>
        </div>
      </div>
    </div>
  );
};
